import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { track } from '../lib/track'

interface Props {
  onClose: () => void
  preSelected?: string | null
}

const IconX = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
)

const IconCheck = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="9 12 11 14 15 10"/>
  </svg>
)

const resources = [
  { id: 'llms-txt', label: 'llms.txt Template', desc: 'Template para indexar seu site em LLMs' },
  { id: 'wp-security', label: 'WP Security Checklist', desc: '40 pontos de hardening WordPress' },
  { id: 'n8n-pipeline', label: 'n8n Enrichment Flow', desc: 'Enriquecimento de leads via Apify, sem Clay' },
  { id: 'gtm-prompts', label: 'Prompt Pack GTM', desc: 'Prompts Claude para prospecção e automação' },
]

const WEBHOOK_URL = import.meta.env.VITE_N8N_WEBHOOK || ''

export default function PopupIsca({ onClose, preSelected = null }: Props) {
  const [selected, setSelected] = useState<string | null>(preSelected)
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')

  useEffect(() => {
    track('popup_open', { trigger: 'exit_intent_or_timer' })
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selected || !email || status === 'loading') return
    setStatus('loading')
    track('popup_submit', { resource: selected })
    try {
      const ok = WEBHOOK_URL
        ? (await fetch(WEBHOOK_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, resource: selected, source: 'matheusedson.com' }),
          })).ok
        : true

      if (ok) track('gift_redeemed', { resource: selected })
      setStatus(ok ? 'done' : 'error')
    } catch {
      setStatus('error')
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(6px)',
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.92, y: 16 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.92, y: 16 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
        style={{
          width: '100%',
          maxWidth: 420,
          background: 'var(--bg2)',
          border: '1px solid var(--border)',
          borderRadius: 16,
          padding: '2rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.25rem',
          position: 'relative',
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            color: 'var(--muted)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0.25rem',
            borderRadius: 4,
          }}
          aria-label="Fechar"
        >
          <IconX />
        </button>

        {status === 'done' ? (
          <div style={{ textAlign: 'center', padding: '1rem 0', display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'center' }}>
            <IconCheck />
            <div style={{ fontWeight: 600 }}>Email a caminho.</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
              Confere sua caixa em alguns minutos.
            </div>
          </div>
        ) : (
          <>
            <div>
              <div style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                Escolhe o que levar:
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
                Ferramentas que uso no dia a dia.
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {resources.map(r => (
                <label
                  key={r.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    border: `1px solid ${selected === r.id ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: 8,
                    background: selected === r.id ? 'rgba(79,126,247,0.08)' : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  <input
                    type="radio"
                    name="resource"
                    value={r.id}
                    checked={selected === r.id}
                    onChange={() => setSelected(r.id)}
                    style={{ marginTop: 2, accentColor: 'var(--accent)' }}
                  />
                  <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>{r.label}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{r.desc}</div>
                  </div>
                </label>
              ))}
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <input
                type="email"
                required
                placeholder="seu@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                style={{
                  padding: '0.65rem 0.9rem',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: 'var(--bg)',
                  color: 'var(--text)',
                  fontSize: '0.9rem',
                  fontFamily: 'inherit',
                  outline: 'none',
                  transition: 'border-color 0.15s',
                }}
                onFocus={e => (e.target.style.borderColor = 'var(--accent)')}
                onBlur={e => (e.target.style.borderColor = 'var(--border)')}
              />

              <button
                type="submit"
                disabled={!selected || !email || status === 'loading'}
                style={{
                  padding: '0.7rem',
                  borderRadius: 8,
                  background: selected && email ? 'var(--accent)' : 'var(--border)',
                  color: 'var(--text)',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  transition: 'all 0.15s',
                  opacity: status === 'loading' ? 0.7 : 1,
                }}
              >
                {status === 'loading' ? 'Enviando...' : 'Manda pra mim'}
              </button>

              {status === 'error' && (
                <div style={{ fontSize: '0.75rem', color: 'var(--red)', textAlign: 'center' }}>
                  Erro ao enviar. Tenta de novo.
                </div>
              )}

              <div style={{ fontSize: '0.7rem', color: 'var(--muted)', textAlign: 'center', opacity: 0.7 }}>
                Sem spam. Só o arquivo.
              </div>
            </form>
          </>
        )}
      </motion.div>
    </motion.div>
  )
}
